// The ERP's host for the AI Companion's chat (rep-app W6, spec section 9
// "Embedding"): the cookie API client, the router plus the zoom breadcrumb
// (a link opened from the chat pushes a frame so Esc returns), the record the
// zoom store names, history in localStorage with its 12-hour auto-clear, and
// a refresh of every query after a draft is created. The Sales app never
// imports this module (rep/__tests__/entryReach.test.ts).
import { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useZoomStore } from '@/shared/store/zoomStore';
import { api } from '@/shared/api/client';
import { CopilotChat } from './CopilotChat';
import type { CopilotHistory, CopilotHost, CopilotMsg } from './host';

// Persisted history (survives drawer close / reload). Auto-clears when the
// last activity is older than AUTO_CLEAR_MS so stale conversations don't pile
// up indefinitely.
export const STORE_KEY = 'nexus:copilot:history:v1';
const AUTO_CLEAR_MS = 12 * 60 * 60 * 1000; // 12 hours

export const erpHistory: CopilotHistory = {
  load() {
    try {
      const raw = localStorage.getItem(STORE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as { ts: number; messages: CopilotMsg[] };
      if (!parsed?.ts || Date.now() - parsed.ts > AUTO_CLEAR_MS) {
        localStorage.removeItem(STORE_KEY);
        return [];
      }
      return Array.isArray(parsed.messages) ? parsed.messages : [];
    } catch { return []; }
  },
  save(messages) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify({ ts: Date.now(), messages })); } catch { /* quota / private mode */ }
  },
  clear() {
    try { localStorage.removeItem(STORE_KEY); } catch { /* noop */ }
  },
};

export function useErpCopilotHost({ context, onNavigate }: { context?: string; onNavigate?: () => void } = {}): CopilotHost {
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  const pushZoom = useZoomStore((s) => s.push);
  // The record the user is currently viewing (e.g. "PRJ-0002") so the
  // assistant can resolve "this project / this invoice / here" to it.
  const currentLabel = useZoomStore((s) => s.currentLabel);
  return useMemo<CopilotHost>(() => ({
    request: api,
    // Open a created record or a page: push a zoom frame so Esc / breadcrumb
    // returns here, close the drawer, then navigate. Never push a frame for a
    // link to the page we're already on: the pathname won't change, so
    // AppLayout's watcher never consumes the intentionalNav flag, and the
    // dangling flag preserved this stale self-frame across the NEXT real
    // navigation (the "Receipts ›" crumb surviving onto Admin Center).
    navigate(link: string) {
      const here = loc.pathname + loc.search;
      if (link !== here && link.split('?')[0] !== loc.pathname) {
        pushZoom({ path: here, label: context || 'Back' });
      }
      onNavigate?.();
      nav(link);
    },
    context: { label: context, record: currentLabel || null, recordPath: loc.pathname },
    history: erpHistory,
    showLinks: true,
    // Refresh any open page (e.g. the project the user is viewing) so the
    // just-created record shows up without a manual reload.
    onExecuted: () => { void qc.invalidateQueries(); },
  }), [nav, loc.pathname, loc.search, qc, pushZoom, currentLabel, context, onNavigate]);
}

export function ErpCopilotChat({ context, onNavigate }: { context?: string; onNavigate?: () => void }) {
  const host = useErpCopilotHost({ context, onNavigate });
  return <CopilotChat host={host} />;
}
